import { Button as ReactButton } from 'devextreme-react/button';
import { Popup } from 'devextreme-react/popup';
import { TextBox } from 'devextreme-react/text-box';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Container } from 'reactstrap';
import configurationApi from '../../api/ConfigurationApi.js';
import userApi from '../../api/UserApi.js';
import '../css/Layout.css';
import { AppProvider } from './AppContext';
import Header from './Header';
import InactivityHandler from './InactivityHandler';
import NavMenu from './NavMenu';
import notify from "devextreme/ui/notify";

const Layout = (props) => {

    const homePage = "/";
    const startPage = "/portfolio";

    const pathname = useLocation().pathname.toLowerCase();
    const originalPath = useLocation().pathname;
    const hash = useLocation().hash;

    const navigate = useNavigate();

    const usernameTextBox = useRef(null);
    const passwordTextBox = useRef(null);

    const configuration = useRef({});
    const attributes = useRef({});

    const [state, setState] = useState({
        configuration: {},
        userInfo: undefined,
        menuKind: 'none',
    });

    const errorToast = (message) => {
        notify(message, 'error', 5000);
    }

    const infoToast = (message) => {
        notify(message, 'info', 5000);
    }

    const [loginVisible, setLoginVisible] = useState(false);

    const [popupMessage, setPopupMessage] = useState({ message: '', class: 'row popupRow hideMessage', height: 260 });
    const [url, setUrl] = useState(pathname);
    const [errorMessage, setErrorMessage] = useState("");

    const popupRef = React.createRef();

    useEffect(() => {
        if (errorMessage) { onError(errorMessage); }
    }, [errorMessage]);

    const registerEnterKeyHandler = (e) => {
        e.component.registerKeyHandler("enter", (e) => {
            onOkClick(e);
        });
    };

    useEffect(() => {
        const splited = pathname.split('/');

        if (pathname == "/error") {
            setState({ menuKind: 'none', configuration: {} });
            return;
        }

        if (pathname.startsWith("/grantdetails/") || pathname.startsWith("/spsync/")) {
            getUser(pathname + hash);
            return;
        }

        if (splited.length > 2) {
            // Don't clear token for known nested routes
            const knownNestedRoutes = ['grantdetails', 'tasklist-templates', 'login', 'spsync'];
            const isKnownNestedRoute = knownNestedRoutes.includes(splited[1]);

            if (!isKnownNestedRoute) {
                userApi.clearToken();
            }

            if (splited[1] === 'testlogin') {
                const userName = splited[2];
                checkUser(userName, undefined);
                return;
            }

            if (splited[1] === 'token') {
                // Logging in via OIDC with token

                const token = originalPath.split('/')[2];
                const url = originalPath.split('/')[3];

                if (url && url !== undefined && url !== 'undefined') {
                    const decodedUrl = decodeURIComponent(url);
                    checkUserOIDC(token, decodedUrl);
                } else {
                    checkUserOIDC(token);
                }

                return;
            }

            if (isKnownNestedRoute) {
                // OIDC callback handles its own auth
                // without this condition, a successful oidc login for an inactive user
                // will just loop back to login when getuser can't find the user
                // and redirect them back to the login page where they're already
                // successfully authenticated via oidc
                if (pathname === '/login/oidc-callback') {
                    return;
                }
                // Handle known nested routes - just get user and continue
                getUser(pathname + hash);
                return;
            }

            if (splited[2] !== 'oidc-callback' && splited[1] !== 'token') {
                setErrorMessage("User name is not recognized.");
            }

        } else {
            if (pathname == "/logout") {
                configuration.current = {};
                attributes.current = {};
                setState({ menuKind: 'none', configuration: {}, userInfo: undefined });
                setUrl(pathname);
                return;
            }

            if (pathname == "/oidcerror") {
                userApi.clearToken();
                hideForm();
                navigate('/error');
                return;
            }

            if (pathname == "/oidcnotfound") {
                userApi.clearToken();
                hideForm();
                navigate('/oidcnotfound');
                return;
            }

            if (pathname == homePage || pathname == startPage) {
                var token = userApi.getAccessToken();
                if (token) {
                    getUser(pathname).then(res => {
                        if (res) {
                            navigate(startPage);
                        }
                        else {
                            doLogin();
                        }
                    });
                } else {
                    doLogin();
                }

                return;
            }

            if (!state.userInfo) {
                getUser(pathname);
                return;
            }
            if (state.userInfo && !state.siteReady) {
                navigate(pathname);
            }

            setUrl(pathname);
        }
    }, [pathname]);

    const doLogin = async (path) => {
        let config = await getConfiguration();

        if (config.useNIHLogin) {
            userApi.oidcSignInRedirect(config, encodeURIComponent(path), onError);
        } else {
            showForm();
        }
    };

    const getConfiguration = async () => {

        if (Object.keys(configuration.current).length === 0) {
            await configurationApi.retrieveConfiguration().then(result => {
                setState(state => ({
                    ...state,
                    configuration: result
                }));
                configuration.current = result;
                return result;
            })
        }

        return Promise.resolve(configuration.current);
    }

    const getUserAttributes = async (process) => {

        if (Object.keys(attributes.current).length === 0) {
            await userApi.getUserAttr().then(async result => {
                let config = await getConfiguration();
                if (result?.userInfo?.userId) {
                    const userInfo = result.userInfo;
                    const menuKind = getMenuKind(pathname, userInfo.permissions);
                    setPopupMessage({ message: '', class: 'row popupRow hideMessage', height: 260 });
                    setState(state => ({
                        ...state,
                        userInfo: result.userInfo,
                        menuKind,
                        loggedIn: true,
                        siteReady: process,
                        configuration: config
                    }));

                    attributes.current = result;
                }

                return result;
            })
        }

        return Promise.resolve(attributes.current);
    }


    const checkUserOIDC = async (token, path) => {
        if (token && token !== '') {
            const result = await userApi.checkUserOIDC(token);

            if (!result) {
                processingUser(path);
            } else if (result === 'Not Found') {
                navigate('/oidcnotfound');
            } else {
                errorToast(result);
            }
        }
    }

    const checkUser = async (userName, path) => {
        if (userName && userName !== '') {
            const result = await userApi.checkUserName(userName);

            if (!result) {
                processingUser(path);
            } else {
                errorToast(result);
            }
        }
    }

    const getUser = async (path) => {
        const userAttr = await getUserAttributes();

        if (userAttr && userAttr.userInfo && userAttr.userInfo.fullName) {
            setUrl(pathname);
            return true;
        } else {
            doLogin(path).then(res => {
                if (res) {
                    getUser(path);
                }
            });
        }
        return false;
    }

    const onError = async (error) => {
        const message = handleError(error);


        if (error == "HTTP error: 401") {
            setPopupMessage({ message: 'Please log in to continue', class: 'row popupRow showMessage', height: 280 });

            navigate(homePage, { state: { props: 'Please log in to continue' } });
        } else {
            errorToast(error);
        }
    }

    const handleError = (error) => {
        if (error == "HTTP error: 401") {
            return "Error authenticating user."
        }

        if (error == "Bad Request") {
            return "Bad Request."
        }
        return error;
    }

    const getMenuKind = (pathname, permissions) => {
        switch (pathname) {
            case "/logout":
                return 'none';

            default:
                return (permissions.allowConfiguration) ? 'admin' : 'main';
        }
    }

    const getNextPage = (path) => {
        if (path === undefined) {
            path = homePage;
        }

        if (path !== undefined && path.startsWith("/grantdetails/")) {
            return path + hash;
        }

        if (path !== homePage && path !== startPage) {
            return path;
        }

        return startPage;
    }

    const processingUser = async (path) => {
        const userAttr = await getUserAttributes(true);

        if (!userAttr) {
            return;
        }

        const nextPage = getNextPage(path);

        hideForm();
        navigate(nextPage);
        setUrl(pathname);
    }

    const showForm = () => {
        setLoginVisible(true);
    }

    const hideForm = () => {
        setLoginVisible(false);
        usernameTextBox.current.instance.option("value", null);
        passwordTextBox.current.instance.option("value", null);
    }

    const onOkClick = (e) => {
        checkUser(usernameTextBox.current.instance.option("value"), pathname);
    };

    return (
        <div style={{ background: '#fff' }} >
            <Header userInfo={state.userInfo}>
                <NavMenu kind={state.menuKind} url={url} role={state.userInfo?.role} />
            </Header>
            <Popup
                ref={popupRef}
                visible={loginVisible}
                onHiding={hideForm}
                dragEnabled={false}
                position="center"
                hideOnOutsideClick={false}
                showCloseButton={false}
                showTitle={true}
                title="Welcome to eCamera"
                width={400}
                height={popupMessage.height}>
                <div className="container-fluid formReqPanelTop">
                    <div className={popupMessage.class}>
                        <div className="dx-field col-12">
                            <span style={{ marginLeft: '60px', color: 'red', fontSize: '12pt' }}> {popupMessage.message}</span>
                        </div>
                    </div>
                    <div className="row popupRow">
                        <div className="dx-field col-12">
                            <div className="dx-field-label">
                                User Name:
                            </div>
                            <div className="popup-name-value">
                                <TextBox
                                    onInitialized={registerEnterKeyHandler}
                                    ref={usernameTextBox}
                                >
                                </TextBox>
                            </div>
                            <div className="dx-field-label">
                                Password:
                            </div>
                            <div className="popup-name-value">
                                <TextBox
                                    mode="password"
                                    name="password"
                                    onInitialized={registerEnterKeyHandler}
                                    ref={passwordTextBox}
                                >
                                </TextBox>
                            </div>
                        </div>
                    </div>
                </div>
                <br />
                <div className="container-fluid formReqPanelTop">
                    <div className="row popupRow">
                        <div className="dx-field col-4">
                            <ReactButton
                                width={100}
                                text="Ok"
                                onClick={onOkClick} />
                        </div>
                    </div>
                </div>
            </Popup>
            <Container tag="main">
                <AppProvider
                    userInfo={state.userInfo}
                    configuration={state.configuration}
                    loggedIn={state.loggedIn}
                    errorToast={errorToast}
                    infoToast={infoToast}
                >
                    {props.children}
                    <InactivityHandler
                        warning={state.configuration.inactivityWarningMilliseconds}
                        logout={state.configuration.inactivityLogoutMilliseconds} ></InactivityHandler>
                </AppProvider>
            </Container>
        </div >
    );
}

export default Layout;
